import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { getHomeInfo } from '../../../actions/homeInfo';
import { getPortalNotice } from '../../../actions/portalNotice';
import type { PortalNoticeState } from '../../../reducers/portalNotice';
import { PortalNotice } from './PortalNotice';
import { DestaquesGrid } from './DestaquesGrid';
import { AcoesRapidasCard, type AcoesRapidasData } from './AcoesRapidasCard';
import { OndeAtuaSection } from './OndeAtuaSection';
import { IniciativasSection } from './IniciativasSection';
import { DestaquesCarrosselSection } from './DestaquesCarrosselSection';
import { PublicacoesEditaisSection } from './PublicacoesEditaisSection';
import { AcompanheRedesSection } from './AcompanheRedesSection';
import {
  DESTAQUES_COUNT,
  destaquesCarrosselHasContent,
  iniciativasHasContent,
  type DestaquesCarrosselData,
  type DestaquesData,
  type IniciativasData,
  type OndeAtuaData,
  type PublicacoesData,
  redesHasContent,
  type RedesData,
} from './helpers';
import { useNewsSearch } from './useNewsSearch';
import {
  type BlocksData,
  getHomeBlockData,
  type HomeInfoState,
} from 'volto-ministerio-publico-federal/helpers/HomeInfo/HomeInfo';

const bannerHasSlides = (banner?: BlocksData): boolean => {
  const blocks = banner?.blocks ?? {};
  return Object.values(blocks).some(
    (block) =>
      block?.['@type'] === 'homeBannerPortal' &&
      Array.isArray(block.items) &&
      block.items.length > 0,
  );
};

const CapaView: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const homeInfo = useSelector(
    (state: { homeInfo: HomeInfoState }) => state.homeInfo,
  );

  const portalNoticeLoaded = useSelector(
    (state: { portalNotice?: PortalNoticeState }) => state.portalNotice?.loaded,
  );

  React.useEffect(() => {
    dispatch(getHomeInfo());
  }, [dispatch]);

  React.useEffect(() => {
    if (!portalNoticeLoaded) dispatch(getPortalNotice());
  }, [dispatch, portalNoticeLoaded]);

  const banner = homeInfo?.items?.banner;
  const home = homeInfo?.items?.home;
  const showDynamicBanner = bannerHasSlides(banner);
  const destaquesData = getHomeBlockData(home, 'mpfDestaques') as
    | DestaquesData
    | undefined;
  const acoesData = getHomeBlockData(home, 'mpfAcoesRapidas') as
    | AcoesRapidasData
    | undefined;
  const ondeAtuaData = getHomeBlockData(home, 'mpfOndeAtua') as
    | OndeAtuaData
    | undefined;
  const iniciativasData = getHomeBlockData(home, 'mpfIniciativas') as
    | IniciativasData
    | undefined;
  const destaquesCarrosselData = getHomeBlockData(
    home,
    'mpfDestaquesCarrossel',
  ) as DestaquesCarrosselData | undefined;
  const publicacoesData = getHomeBlockData(home, 'mpfPublicacoesEditais') as
    | PublicacoesData
    | undefined;
  const redesData = getHomeBlockData(home, 'mpfAcompanheRedes') as
    | RedesData
    | undefined;

  const { items: news, loading, loaded } = useNewsSearch(DESTAQUES_COUNT);

  return (
    <div className="__container">
      <h1 className="sr-only">Portal do Ministério Público Federal</h1>

      <PortalNotice />

      {showDynamicBanner && (
        <div className="mt-4 sm:mt-6">
          <RenderBlocks content={banner} location={location} />
        </div>
      )}

      <div className="mpf-secoes">
        <section
          aria-labelledby="capa-faixa-titulo"
          className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12"
        >
          <h2 id="capa-faixa-titulo" className="sr-only">
            Destaques e serviços
          </h2>

          <div className="order-1 min-w-0 lg:col-span-8">
            <DestaquesGrid
              data={destaquesData}
              fallback={news}
              loading={loading || !loaded}
            />
          </div>

          <div className="order-2 min-w-0 lg:col-span-4">
            <AcoesRapidasCard data={acoesData} />
          </div>
        </section>

        <OndeAtuaSection data={ondeAtuaData} />

        {(iniciativasHasContent(iniciativasData) ||
          destaquesCarrosselHasContent(destaquesCarrosselData)) && (
          <section
            aria-labelledby="capa-iniciativas-faixa-titulo"
            className="grid grid-cols-1 gap-6 lg:grid-cols-12"
          >
            <h2 id="capa-iniciativas-faixa-titulo" className="sr-only">
              Iniciativas, projetos e destaques
            </h2>

            <div className="order-1 min-w-0 lg:col-span-8">
              <IniciativasSection data={iniciativasData} />
            </div>

            <div className="order-2 min-w-0 lg:col-span-4">
              <DestaquesCarrosselSection data={destaquesCarrosselData} />
            </div>
          </section>
        )}

        <PublicacoesEditaisSection data={publicacoesData} />

        {redesHasContent(redesData) && (
          <AcompanheRedesSection data={redesData} />
        )}
      </div>
    </div>
  );
};

export default CapaView;
export { CapaView };
